import { IPagedCollection } from '@moko/utils';
import { internalFetch, postCsv } from '../utils/fetchHelper';
import type { IImportCsvRow } from '../utils/csvHelpers';

export interface IContractor {
  id: number;
  name: string;
  code: string;
  works: any;
  createdAt: string;
  otherInfo: string;
  groupId?: number;
  worksIds?: number[];
}

export interface IUpdateContractorRequest {
  name: string;
  code: string;
  // Optional on update: assignments are managed independently via the
  // /p/workAssignment endpoint (immediate add/remove). Sending an array here
  // makes the backend REPLACE the assignment set, so we omit it on edit to
  // avoid wiping assignments the user added through the grid.
  worksIds?: number[];
  otherInfo: string;
  description?: string;
  groupId?: number;
}

export interface ICreateContractorRequest {
  name: string;
  code: string;
  worksIds: number[];
  otherInfo: string;
  assignmentsDescription: string;
  groupId?: number;
}

export interface ICreateContractorResponse {
  contractorId: number;
}

export const getContractors = async (
  abortController: AbortController,
  pageSize: number,
  pageNo: number,
  orderBy?: string,
  search?: string
): Promise<IPagedCollection<IContractor>> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/contractors?pageSize=${pageSize}&pageNo=${pageNo}${
      search ? `&search=${search}` : ''
    }${orderBy ? `&orderBy=${orderBy}` : ''}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController
  );
};

export const getContractor = async (
  abortController: AbortController,
  contractorId?: number,
  contractorCode?: string
): Promise<IContractor> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/contractor?${
      contractorId
        ? `contractorId=${contractorId}`
        : `contractorCode=${contractorCode}`
    }&code=${import.meta.env.VITE_BACKEND_API_KEY}`,
    'GET',
    abortController
  );
};

export const editContractor = async (
  abortController: AbortController,
  contractorId: number,
  contractor: IUpdateContractorRequest
): Promise<number> => {
  return internalFetch(
    `${
      import.meta.env.VITE_BACKEND_API_URL
    }/p/contractor?contractorId=${contractorId}&code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'PUT',
    abortController,
    JSON.stringify(contractor)
  );
};

export const createContractor = async (
  abortController: AbortController,
  data: ICreateContractorRequest
): Promise<ICreateContractorResponse> => {
  return internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/contractor`,
    'POST',
    abortController,
    JSON.stringify(data)
  );
};

export interface IImportContractorsResponse {
  importContractorResponse: IImportCsvRow[];
  createdCount: number;
  updatedCount: number;
  messages: string[];
  errorsCount: number;
}

export const importContractors = (
  abortController: AbortController,
  csvContent: string
): Promise<IImportContractorsResponse> =>
  postCsv<IImportContractorsResponse>(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/importContractors`,
    csvContent,
    abortController
  );

export const exportContractors = async (
  abortController: AbortController
): Promise<string> => {
  const response: Response = await internalFetch(
    `${import.meta.env.VITE_BACKEND_API_URL}/p/exportContractors?code=${
      import.meta.env.VITE_BACKEND_API_KEY
    }`,
    'GET',
    abortController,
    undefined,
    true
  );

  return response.text();
};
